import React from "react";
import Icon from "assets/icons";
import classNames from "classnames";
import { FormikProps } from "formik";
import FormContainer from "shared/form";
import { InputType } from "shared/form/enum";
import Button from "shared/components/button";
import Direction from "shared/enums/Direction";
import useResetPasswordService from "./service";
import ButtonType from "shared/enums/ButtonTypes";
import { useCopies } from "shared/hooks/useCopies";
import { passwordRequirementChecks } from "./utils";
import FormTextField from "shared/form/fields/textField";
import useOnOutSideClick from "shared/hooks/useOnOutSideClick";
import WavyBackground from "shared/components/wavyBackground";

export default function ResetPassword() {
  const copies = useCopies();
  const service = useResetPasswordService();
  const [focused, setFocused] = React.useState(false);
  const passwordFieldRef = React.useRef<HTMLDivElement>(null);
  useOnOutSideClick({
    node: passwordFieldRef.current,
    onOutSideClick: () => {
      setFocused(false);
    }
  });

  return (
    <div className="loginWrapper">
      <div className="login_wavyBackground">
        <WavyBackground />
      </div>
      <div className="login_waterMark">
        <Icon.Personatech />
      </div>
      <div className="login_box login_box--resetPassword">
        <div className="login_box_content">
          <h1>{service.formHelper.headerText}</h1>
        </div>
        <div className="authenticationCard">
          <div className="authenticationCard_forgotPass--form">
            <h3 className="forgotPassword_title">{copies.get("confirm.password")}</h3>
            <p className="forgotPassword_subTitle">{copies.get("console.reset.password.message")}</p>
            <FormContainer
              initialValues={service.formHelper.initialValues}
              validationSchema={service.formHelper.validationSchema}
              onSubmit={service.formHelper.handleSubmit}>
              {(formikProps: FormikProps<any>) => (
                <FormContainer.Form className="loginForm form--fullWidth">
                  <div ref={passwordFieldRef} className="resetPasswordField">
                    <FormTextField
                      name="password"
                      switchIconPosition={true}
                      label={copies.get("password")}
                      type={service.displayPassword ? InputType.TEXT : InputType.PASSWORD}
                      onFocus={() => {
                        setFocused(true);
                      }}
                      icon={
                        <Button
                          icon={service.displayPassword ? <Icon.VisibilityOff /> : <Icon.VisibilityOn />}
                          onClick={() => service.setDisplayPassword(!service.displayPassword)}
                          title={copies.get("show")}
                        />
                      }
                    />
                    <PasswordRequirementTooltip
                      isPasswordTooltipOpen={focused}
                      requirementTooltipDirection={Direction.LEFT}
                      password={formikProps.values.password}
                    />
                  </div>
                  <FormTextField
                    name="confirmPassword"
                    label={copies.get("confirm.password")}
                    type={InputType.PASSWORD}
                  />
                  <div className="authenticationCard_actions text-center">
                    <Button
                      className="loginbtn_stylized"
                      label={service.formHelper.submitButtonText}
                      type={ButtonType.SUBMIT}
                    />
                  </div>
                </FormContainer.Form>
              )}
            </FormContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PasswordRequirementTooltip(props: {
  password: string;
  className?: string;
  isPasswordTooltipOpen: boolean;
  requirementTooltipDirection?: Direction;
}) {
  const copies = useCopies();
  const passwordCheckList = React.useMemo(() => passwordRequirementChecks(props.password), [props.password]);
  const computedClassNames = React.useMemo(
    () =>
      classNames(props.className, {
        login_box_resetPassword_requirements: true,
        [`login_box_resetPassword_requirements--${props.requirementTooltipDirection}`]:
          props.requirementTooltipDirection
      }),
    [props.className, props.requirementTooltipDirection]
  );
  if (!props.isPasswordTooltipOpen) {
    return null;
  }
  return (
    <div className={computedClassNames}>
      <div className="validatePasswordTitle">{copies.get("password.requirement")}</div>
      <ul className="validatePasswordWrapper">
        {passwordCheckList.map((check, index) => (
          <li key={index}>
            <div
              className={classNames("validatePasswordItem", {
                validateSuccess: check.success
              })}>
              <span className="validatePasswordItem_icon">
                <span className="validatePasswordItem_icon--circle">
                  <Icon.Check />{" "}
                </span>
              </span>
              <span className="validatePasswordItem_text">{copies.get(check.copyKey)}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
